/**
 * Static mocks for offline UX preview. Shapes match types/domain.ts.
 * The "delay" wrappers mimic real network so skeletons & loading states are exercised.
 */

import type {
  Account,
  AccountCheck,
  AccountDialog,
  AccountLogEntry,
  Campaign,
  CampaignAccountAssignment,
  CampaignAccountRole,
  CampaignTeamMember,
  CampaignHistoryResponse,
  CampaignLogsResponse,
  CampaignLaunchExecuteResult,
  CampaignLaunchPreview,
  CampaignLaunchPreviewItem,
  CampaignStatus,
  ChatHealth,
  DialogTemplate,
  Incoming,
  LaunchMode,
  ManualDialogStepInput,
  PaginatedAccountLogs,
  Report,
  Target,
  TargetBulkDeleteResult,
  TargetDeletePreview,
} from '@/types/domain';

const wait = <T>(v: T, ms = 280): Promise<T> => new Promise((r) => setTimeout(() => r(v), ms));

const campaigns: Campaign[] = [
  {
    id: 1,
    name: 'Mining pool · 44pool RU',
    status: 'running',
    dialogPrompt:
      'Естественный разговор о выборе майнинг-пула. Тема: низкая комиссия, ежедневные выплаты, отсутствие KYC. В последнем сообщении B упоминает рефералку.',
    dmReplyPrompt:
      'Если пишут в ЛС или пингуют — отвечать дружелюбно, как опытный майнер. Не продавать в лоб. Дать рефку только по запросу.',
    scheduleStartHour: 10,
    scheduleEndHour: 22,
    intervalMinutes: 75,
    maxChatsPerAccountPerDay: 8,
    minDelayBetweenChatsSec: 600,
    joinDelayMinSec: 60,
    joinDelayMaxSec: 240,
    typingMinMs: 2500,
    typingMaxMs: 9000,
    seedStepDelayMinSec: 20,
    seedStepDelayMaxSec: 180,
    chatReplyDelayMinSec: 20,
    chatReplyDelayMaxSec: 180,
    dmReplyDelayMinSec: 60,
    dmReplyDelayMaxSec: 300,
    maxChatRepliesPerUser: 1,
    maxDmRepliesPerDialog: 1,
    seedMessagesMin: 2,
    seedMessagesMax: 5,
    uniqueDialogPerChat: true,
    createdAt: new Date(Date.now() - 86400e3 * 4).toISOString(),
    counts: { targets: 32, accounts: 24, dialogs: 9, posts: 184, pings: 47, dms: 12 },
    todayPosts: 23,
    todayLimit: 60,
    seeding: {
      readyTargets: 22,
      trackedTargets: 18,
      completedTargets: 15,
      postedTargets: 14,
      activeTargets: 3,
      failedTargets: 1,
      tasksScheduled: 2,
      tasksRunning: 1,
      tasksPosted: 14,
      tasksFailed: 1,
      targetStatuses: [
        {
          targetId: 101,
          title: 'Crypto Miners RU',
          normalizedLink: '@cryptominers_ru',
          status: 'posted',
          messagesPosted: 4,
          scheduledAt: new Date(Date.now() - 3600e3 * 4).toISOString(),
          startedAt: new Date(Date.now() - 3600e3 * 3.7).toISOString(),
          finishedAt: new Date(Date.now() - 3600e3 * 3.2).toISOString(),
          lastError: null,
        },
        {
          targetId: 102,
          title: 'ASIC Chat',
          normalizedLink: '@asicchat',
          status: 'running',
          messagesPosted: 2,
          scheduledAt: new Date(Date.now() - 1800e3).toISOString(),
          startedAt: new Date(Date.now() - 900e3).toISOString(),
          finishedAt: null,
          lastError: null,
        },
        {
          targetId: 103,
          title: 'Farm Talk',
          normalizedLink: '@farmtalk',
          status: 'failed',
          messagesPosted: 1,
          scheduledAt: new Date(Date.now() - 7200e3).toISOString(),
          startedAt: new Date(Date.now() - 6900e3).toISOString(),
          finishedAt: new Date(Date.now() - 6800e3).toISOString(),
          lastError: 'Flood wait',
        },
      ],
    },
  },
  {
    id: 2,
    name: 'Crypto onboarding · CIS',
    status: 'ready',
    scheduleStartHour: 9,
    scheduleEndHour: 21,
    intervalMinutes: 60,
    maxChatsPerAccountPerDay: 6,
    minDelayBetweenChatsSec: 900,
    joinDelayMinSec: 60,
    joinDelayMaxSec: 240,
    typingMinMs: 3000,
    typingMaxMs: 10000,
    seedStepDelayMinSec: 30,
    seedStepDelayMaxSec: 210,
    chatReplyDelayMinSec: 30,
    chatReplyDelayMaxSec: 240,
    dmReplyDelayMinSec: 90,
    dmReplyDelayMaxSec: 360,
    maxChatRepliesPerUser: 1,
    maxDmRepliesPerDialog: 1,
    seedMessagesMin: 2,
    seedMessagesMax: 4,
    uniqueDialogPerChat: true,
    createdAt: new Date(Date.now() - 86400e3 * 9).toISOString(),
    counts: { targets: 18, accounts: 14, dialogs: 5, posts: 0, pings: 0, dms: 0 },
    seeding: {
      readyTargets: 0,
      trackedTargets: 0,
      completedTargets: 0,
      postedTargets: 0,
      activeTargets: 0,
      failedTargets: 0,
      tasksScheduled: 0,
      tasksRunning: 0,
      tasksPosted: 0,
      tasksFailed: 0,
      targetStatuses: [],
    },
  },
  {
    id: 3,
    name: 'Smm-агентство · опросы',
    status: 'draft',
    scheduleStartHour: 11,
    scheduleEndHour: 20,
    intervalMinutes: 90,
    maxChatsPerAccountPerDay: 5,
    minDelayBetweenChatsSec: 1200,
    joinDelayMinSec: 60,
    joinDelayMaxSec: 240,
    typingMinMs: 2500,
    typingMaxMs: 9000,
    seedStepDelayMinSec: 20,
    seedStepDelayMaxSec: 180,
    chatReplyDelayMinSec: 20,
    chatReplyDelayMaxSec: 120,
    dmReplyDelayMinSec: 60,
    dmReplyDelayMaxSec: 240,
    maxChatRepliesPerUser: 1,
    maxDmRepliesPerDialog: 1,
    seedMessagesMin: 2,
    seedMessagesMax: 5,
    uniqueDialogPerChat: false,
    createdAt: new Date(Date.now() - 3600e3 * 6).toISOString(),
    counts: { targets: 6, accounts: 0, dialogs: 0, posts: 0, pings: 0, dms: 0 },
    seeding: {
      readyTargets: 0,
      trackedTargets: 0,
      completedTargets: 0,
      postedTargets: 0,
      activeTargets: 0,
      failedTargets: 0,
      tasksScheduled: 0,
      tasksRunning: 0,
      tasksPosted: 0,
      tasksFailed: 0,
      targetStatuses: [],
    },
  },
];

const accounts: Account[] = [
  { id: 11, username: 'kostya_88', phone: '+79261234567', name: 'Костя', status: 'active', banCount: 0, proxy: { ip: '154.13.220.5', port: 1080 }, lastSeenAt: new Date().toISOString() },
  { id: 12, username: 'alex.miner', phone: '+79268887766', name: 'Алекс', status: 'active', banCount: 0, proxy: { ip: '154.13.220.6', port: 1080 }, lastSeenAt: new Date().toISOString() },
  { id: 13, username: 'mariadev', phone: '+79091112233', name: 'Мария', status: 'cooldown', cooldownUntil: new Date(Date.now() + 1800e3).toISOString(), banCount: 1, proxy: { ip: '154.13.220.7', port: 1080 } },
  { id: 14, username: 'denver.t',  phone: '+79161234455', name: 'Денвер', status: 'active', banCount: 0, proxy: { ip: '154.13.220.8', port: 1080 }, lastSeenAt: new Date().toISOString() },
  { id: 15, username: 'stas_v',    phone: '+79061234411', name: 'Стас',   status: 'banned', banCount: 3, proxy: null },
  { id: 16, username: 'lana_pro',  phone: '+79261112299', name: 'Лана',   status: 'active', banCount: 0, proxy: { ip: '154.13.220.9', port: 1080 }, lastSeenAt: new Date(Date.now() - 240e3).toISOString() },
];

const attachedAccountsByCampaign = new Map<number, Map<number, CampaignAccountRole>>([
  [1, new Map([[11, 'A'], [12, 'B']])],
  [2, new Map([[16, null]])],
  [3, new Map()],
]);

const targets: Record<number, Target[]> = {
  1: [
    { id: 101, campaignId: 1, rawInput: 'https://t.me/cryptominers_ru', normalizedLink: '@cryptominers_ru', title: 'Crypto Miners RU', membersCount: 14_320, isJoined: true, health: 'alive', healthScore: 0.78, targetAudienceScore: 0.62, spamRatio: 0.08, messagesPerDay: 280 },
    { id: 102, campaignId: 1, rawInput: 'https://t.me/asicchat',          normalizedLink: '@asicchat',          title: 'ASIC Chat',         membersCount: 8_120,  isJoined: true, health: 'quiet', healthScore: 0.58, targetAudienceScore: 0.55, spamRatio: 0.12, messagesPerDay: 145, healthReason: 'Есть разговор, но обсуждения короткие и идут редкими пачками.' },
    { id: 103, campaignId: 1, rawInput: 'https://t.me/farmtalk',          normalizedLink: '@farmtalk',          title: 'Farm Talk',         membersCount: 4_550,  isJoined: true, health: 'spam', healthScore: 0.32, spamRatio: 0.61, messagesPerDay: 64, healthReason: 'Лента забита ссылками, бонусами и промо-пушами.' },
    { id: 104, campaignId: 1, rawInput: 'https://t.me/poolxchange',       normalizedLink: '@poolxchange',       title: 'Pool Exchange',     membersCount: 2_120,  isJoined: false, health: 'pending' },
    { id: 105, campaignId: 1, rawInput: 'https://t.me/dead_pool_chat',    normalizedLink: '@dead_pool_chat',    title: 'Dead Pool Chat',    membersCount: 920,    isJoined: false, health: 'dead', healthScore: 0.12, messagesPerDay: 2, healthReason: 'За последние сообщения почти нет живого человеческого диалога.' },
  ],
};

const dialogs: Record<number, DialogTemplate[]> = {
  1: [
    {
      id: 201,
      campaignId: 1,
      status: 'approved',
      createdAt: new Date(Date.now() - 3600e3 * 12).toISOString(),
      steps: [
        { id: 1, orderIndex: 0, role: 'A', text: 'Ребят, кто какой майнинг-пул юзает? Подустал от 2miners — комса кусается.' },
        { id: 2, orderIndex: 1, role: 'B', text: 'Я с 44pool второй месяц. Норм заходит.', replyToIdx: 0 },
        { id: 3, orderIndex: 2, role: 'A', text: 'А почему его? Не слышал такого.', replyToIdx: 1 },
        { id: 4, orderIndex: 3, role: 'B', text: 'Комиссии 0.5% + ежедневные выплаты, без KYC. Подключение — две минуты.', replyToIdx: 2 },
        { id: 5, orderIndex: 4, role: 'B', text: 'Если кому надо — могу рефкой кинуть)', replyToIdx: 2, isPitch: true },
      ],
    },
    {
      id: 202,
      campaignId: 1,
      status: 'draft',
      createdAt: new Date(Date.now() - 3600e3 * 4).toISOString(),
      steps: [
        { id: 11, orderIndex: 0, role: 'A', text: 'Кто-нибудь пробовал 44pool? Думаю свалить с f2pool.' },
        { id: 12, orderIndex: 1, role: 'B', text: 'Я там. Выплаты каждый день, минималка низкая.', replyToIdx: 0 },
        { id: 13, orderIndex: 2, role: 'A', text: 'Звучит как реклама)', replyToIdx: 1 },
        { id: 14, orderIndex: 3, role: 'B', text: 'Да нет, реально удобно. Без KYC, можно с любым кошельком.', replyToIdx: 2 },
        { id: 15, orderIndex: 4, role: 'B', text: 'Если интересно — кину рефку, там бонус', replyToIdx: 3, isPitch: true },
      ],
    },
    {
      id: 203,
      campaignId: 1,
      status: 'draft',
      createdAt: new Date(Date.now() - 1200e3).toISOString(),
      steps: [
        { id: 21, orderIndex: 0, role: 'A', text: 'У кого реально низкие комиссии на пуле? Майню эфир-классик.' },
        { id: 22, orderIndex: 1, role: 'B', text: 'У меня на 44pool 0.5%. Дешевле не видел.', replyToIdx: 0 },
        { id: 23, orderIndex: 2, role: 'A', text: 'Hash-rate стабильный?', replyToIdx: 1 },
        { id: 24, orderIndex: 3, role: 'B', text: 'Стабильно держит, отвалов почти нет.', replyToIdx: 2 },
        { id: 25, orderIndex: 4, role: 'B', text: 'Кому интересно зайти — пингани в ЛС, скину рефку', replyToIdx: 2, isPitch: true },
      ],
    },
  ],
};

const inbox: Incoming[] = [
  {
    id: 301, campaignName: 'Mining pool · 44pool RU', accountUsername: '@alex.miner',
    fromName: 'Игорь Сипкин', fromUsername: '@isipkin', kind: 'dm',
    text: 'Привет! Можешь рефку на 44pool кинуть?',
    receivedAt: new Date(Date.now() - 60e3 * 3).toISOString(),
    aiReply: { text: 'Привет! Конечно, держи: https://44pool.com/r/aa12. Если что — пиши, помогу настроить.', status: 'posted' },
  },
  {
    id: 302, campaignName: 'Mining pool · 44pool RU', accountUsername: '@kostya_88',
    fromName: 'Виктор', fromUsername: '@vk_sk', kind: 'mention',
    text: '@kostya_88 а ты сам реально на 44pool сидишь?',
    receivedAt: new Date(Date.now() - 60e3 * 12).toISOString(),
    aiReply: { text: 'Ага, четвёртый месяц уже. До этого был 2miners — вообще другой расклад по выплатам.', status: 'posted' },
  },
  {
    id: 303, campaignName: 'Crypto onboarding · CIS', accountUsername: '@lana_pro',
    fromName: 'Денис', fromUsername: '@den_invest', kind: 'reply',
    text: 'А подскажи, где новичку лучше начать?',
    receivedAt: new Date(Date.now() - 60e3 * 28).toISOString(),
    aiReply: { text: '...', status: 'pending' },
  },
];

const reports: Record<number, Report[]> = {
  1: [
    { id: 401, campaignId: 1, generatedAt: new Date(Date.now() - 86400e3).toISOString(), totalPosts: 142, totalPings: 31, totalDMs: 9, filePath: 'reports/c1-2026-05-11.xlsx' },
    { id: 402, campaignId: 1, generatedAt: new Date(Date.now() - 3 * 86400e3).toISOString(), totalPosts: 88, totalPings: 14, totalDMs: 4, filePath: 'reports/c1-2026-05-09.xlsx' },
  ],
};

// ---------- public mock API ----------

export const listCampaigns = () => wait(campaigns);
export const getCampaign = (id: number) =>
  wait(campaigns.find((c) => c.id === id) ?? campaigns[0]);
export const updateCampaign = (id: number, patch: Partial<Campaign>) => {
  const c = campaigns.find((x) => x.id === id);
  if (c) Object.assign(c, patch);
  return wait(c!);
};
export const setCampaignStatus = (id: number, status: CampaignStatus) =>
  updateCampaign(id, { status });

export const previewCampaignLaunch = (
  campaignId: number,
  mode: LaunchMode,
): Promise<CampaignLaunchPreview> => {
  const campaign = campaigns.find((item) => item.id === campaignId) ?? campaigns[0];
  const campaignTargets = targets[campaignId] ?? [];
  if (mode === 'join') {
    const items: CampaignLaunchPreviewItem[] = campaignTargets.map((target, index) => ({
      targetId: target.id,
      title: target.title || target.normalizedLink,
      normalizedLink: target.normalizedLink,
      currentCoverage: target.isJoined ? 2 : 0,
      reservedCount: target.isJoined ? 2 : 0,
      candidateAccountIds: target.isJoined ? [] : [11 + (index % 2), 12 + (index % 2)],
      status: target.isJoined ? 'already_joined' : 'ready',
    }));
    const readyItems = items.filter((item) => item.status === 'ready');
    return wait({
      mode,
      summary: {
        targets: items.length,
        accounts: 4,
        readyTargets: readyItems.length,
        skippedTargets: items.length - readyItems.length,
        tasks: readyItems.reduce((sum, item) => sum + (item.candidateAccountIds?.length ?? 0), 0),
      },
      items,
    });
  }

  const items: CampaignLaunchPreviewItem[] = campaignTargets.map((target) => ({
    targetId: target.id,
    title: target.title || target.normalizedLink,
    normalizedLink: target.normalizedLink,
    joinedCount: target.isJoined ? 2 : 0,
    seededCount: target.isJoined && campaign.status !== 'draft' ? 1 : 0,
    status: !target.isJoined
      ? 'not_joined'
      : mode === 'seed_new' && target.id % 2 === 0
        ? 'already_seeded'
        : 'ready',
  }));
  const readyItems = items.filter((item) => item.status === 'ready');
  return wait({
    mode,
    summary: {
      targets: items.length,
      readyTargets: readyItems.length,
      skippedTargets: items.length - readyItems.length,
      tasks: readyItems.length,
    },
    items,
  });
};

export const executeCampaignLaunch = (
  campaignId: number,
  mode: LaunchMode,
): Promise<CampaignLaunchExecuteResult> => {
  const campaign = campaigns.find((item) => item.id === campaignId);
  if (campaign && mode !== 'join') campaign.status = 'running';
  const preview = ((): CampaignLaunchPreview => {
    const campaignTargets = targets[campaignId] ?? [];
    if (mode === 'join') {
      const ready = campaignTargets.filter((target) => !target.isJoined);
      return {
        mode,
        summary: {
          targets: campaignTargets.length,
          accounts: 4,
          readyTargets: ready.length,
          skippedTargets: campaignTargets.length - ready.length,
          tasks: ready.length * 2,
        },
        items: [],
      };
    }
    const ready = campaignTargets.filter(
      (target) => target.isJoined && (target.health === 'alive' || target.health === 'unknown'),
    );
    return {
      mode,
      summary: {
        targets: campaignTargets.length,
        readyTargets: ready.length,
        skippedTargets: campaignTargets.length - ready.length,
        tasks: ready.length,
      },
      items: [],
    };
  })();

  return wait(
    mode === 'join'
      ? { mode, scheduled: preview.summary.tasks, targets: preview.summary.readyTargets }
      : { mode, created: preview.summary.tasks, targets: preview.summary.readyTargets },
    700,
  );
};
export const listTargets = (campaignId: number) => wait(targets[campaignId] ?? []);
export const addTargets = (_campaignId: number, raw: string[]) =>
  wait({ added: raw.length, duplicates: 0, invalid: 0 });
export const evaluateAllTargets = (
  _campaignId: number,
  payload?: { audienceDescription?: string; taSignals?: string[] },
) =>
  wait({
    ok: true,
    audienceDescription: payload?.audienceDescription?.trim() || null,
    taSignals: payload?.taSignals ?? [],
  });
export const previewTargetDelete = (
  campaignId: number,
  health: ChatHealth[],
): Promise<TargetDeletePreview> => {
  const selectedHealth = Array.from(new Set(health));
  const items = (targets[campaignId] ?? []).filter((target) => selectedHealth.includes(target.health));
  return wait({
    filters: { health: selectedHealth },
    summary: {
      matched: items.length,
      selectedStatuses: selectedHealth.length,
      byHealth: selectedHealth.map((status) => ({
        health: status,
        count: items.filter((target) => target.health === status).length,
      })),
    },
    items,
  });
};
export const bulkDeleteTargets = (
  campaignId: number,
  targetIds: number[],
): Promise<TargetBulkDeleteResult> => {
  const arr = targets[campaignId] ?? [];
  const ids = Array.from(new Set(targetIds));
  const before = arr.length;
  targets[campaignId] = arr.filter((target) => !ids.includes(target.id));
  const deleted = before - targets[campaignId].length;
  syncCampaignTargetCount(campaignId);
  return wait({ deleted, requested: ids.length });
};
export const listAccounts = () => wait(accounts);
export const getAccount = (accountId: number) =>
  wait(accounts.find((account) => account.id === accountId) ?? accounts[0]);
export const attachAccounts = (campaignId: number, items: CampaignAccountAssignment[]) => {
  const attached = ensureAttachedMap(campaignId);
  let added = 0;
  let updated = 0;

  for (const item of items) {
    const accountId = Number(item.accountId);
    if (!Number.isFinite(accountId)) continue;
    const role: CampaignAccountRole = item.role === 'A' || item.role === 'B' ? item.role : null;
    if (attached.has(accountId)) updated++;
    else added++;
    attached.set(accountId, role);
  }

  syncCampaignAccountCount(campaignId);
  return wait({ added, updated });
};
export const listDialogs = (campaignId: number) => wait(dialogs[campaignId] ?? []);
export const generateDialogs = (campaignId: number, count: number) => {
  // если уже есть — вернём как есть, иначе сгенерим N болванок
  if (dialogs[campaignId]?.length) return wait(dialogs[campaignId]);
  const seedSteps = [
    'Привет всем, кто в теме?',
    'Я недавно с этим разобрался — могу посоветовать.',
    'А что именно используете?',
    'Удобно, дёшево, без лишних телодвижений. Если интересно — напишите, поделюсь рефкой.',
  ];
  const made: DialogTemplate[] = Array.from({ length: count }, (_, i) => ({
    id: 9000 + Math.floor(Math.random() * 1000) + i,
    campaignId,
    status: 'draft',
    createdAt: new Date(Date.now() - i * 60e3).toISOString(),
    steps: seedSteps.map((text, idx) => ({
      id: i * 100 + idx,
      orderIndex: idx,
      role: idx % 2 === 0 ? 'A' : 'B',
      text,
      replyToIdx: idx > 0 ? idx - 1 : null,
      isPitch: idx === seedSteps.length - 1,
    })),
  }));
  dialogs[campaignId] = made;
  return wait(made, 1200);
};
export const createManualDialog = (campaignId: number, steps: ManualDialogStepInput[]) => {
  const existing = dialogs[campaignId] ?? [];
  const nextId = Math.max(0, ...Object.values(dialogs).flat().map((item) => item.id)) + 1;
  const nextStepId =
    Math.max(0, ...Object.values(dialogs).flat().flatMap((item) => item.steps.map((step) => step.id))) +
    1;
  const template: DialogTemplate = {
    id: nextId,
    campaignId,
    status: 'draft',
    createdAt: new Date().toISOString(),
    steps: steps.map((step, index) => ({
      id: nextStepId + index,
      orderIndex: index,
      role: index === 0 ? 'A' : step.role,
      text: step.text,
      replyToIdx: index === 0 ? null : (step.replyToIdx ?? index - 1),
      isPitch: Boolean(step.isPitch),
    })),
  };
  dialogs[campaignId] = [template, ...existing];
  return wait(template, 320);
};
export const approveDialog = (id: number) => {
  const d = Object.values(dialogs).flat().find((x) => x.id === id);
  if (d) d.status = 'approved';
  return wait(d!);
};
export const rejectDialog = (id: number) => {
  const d = Object.values(dialogs).flat().find((x) => x.id === id);
  if (d) d.status = 'rejected';
  return wait(d!);
};
export const regenerateDialog = (id: number, _hint?: string) => {
  const d = Object.values(dialogs).flat().find((x) => x.id === id);
  return wait(d!);
};
export const listIncoming = () => wait(inbox);
export const listReports = (campaignId: number) => wait(reports[campaignId] ?? []);
export const generateReport = (campaignId: number) => {
  const r: Report = {
    id: Math.floor(Math.random() * 10000),
    campaignId,
    generatedAt: new Date().toISOString(),
    totalPosts: 0, totalPings: 0, totalDMs: 0,
    filePath: `reports/c${campaignId}-${new Date().toISOString().slice(0, 10)}.xlsx`,
  };
  reports[campaignId] = [r, ...(reports[campaignId] ?? [])];
  return wait(r, 800);
};


// ---------- INBOX THREADS (агрегаты ЛС/упоминаний по кампании) ----------
import type { InboxThread, ChatMessage } from '@/types/domain';

const threads: Record<number, InboxThread[]> = {
  1: [
    {
      id: '1-301',
      campaignId: 1,
      campaignName: 'Mining pool · 44pool RU',
      accountUsername: '@alex.miner',
      fromName: 'Игорь Сипкин',
      fromUsername: '@isipkin',
      kind: 'dm',
      unreadCount: 0,
      lastAt: new Date(Date.now() - 60e3 * 3).toISOString(),
      preview: 'Привет! Можешь рефку на 44pool кинуть?',
      messages: [
        { id: 1, side: 'them', text: 'Привет! Можешь рефку на 44pool кинуть?', at: new Date(Date.now() - 60e3 * 4).toISOString() },
        { id: 2, side: 'ai',   text: 'Привет! Конечно, держи: https://44pool.com/r/aa12. Если что — пиши, помогу настроить.', at: new Date(Date.now() - 60e3 * 3).toISOString(), status: 'posted' },
        { id: 3, side: 'them', text: 'А там по ETH правда комса 0.5%?', at: new Date(Date.now() - 60e3 * 2).toISOString() },
      ],
    },
    {
      id: '1-302',
      campaignId: 1,
      campaignName: 'Mining pool · 44pool RU',
      accountUsername: '@kostya_88',
      fromName: 'Виктор',
      fromUsername: '@vk_sk',
      kind: 'mention',
      unreadCount: 1,
      lastAt: new Date(Date.now() - 60e3 * 12).toISOString(),
      preview: '@kostya_88 а ты сам реально на 44pool сидишь?',
      messages: [
        { id: 11, side: 'them', text: '@kostya_88 а ты сам реально на 44pool сидишь?', at: new Date(Date.now() - 60e3 * 13).toISOString() },
        { id: 12, side: 'ai',   text: 'Ага, четвёртый месяц уже. До этого был 2miners — вообще другой расклад по выплатам.', at: new Date(Date.now() - 60e3 * 12).toISOString(), status: 'posted' },
      ],
    },
  ],
  2: [
    {
      id: '2-303',
      campaignId: 2,
      campaignName: 'Crypto onboarding · CIS',
      accountUsername: '@lana_pro',
      fromName: 'Денис',
      fromUsername: '@den_invest',
      kind: 'reply',
      unreadCount: 1,
      lastAt: new Date(Date.now() - 60e3 * 28).toISOString(),
      preview: 'А подскажи, где новичку лучше начать?',
      messages: [
        { id: 21, side: 'them', text: 'А подскажи, где новичку лучше начать?', at: new Date(Date.now() - 60e3 * 28).toISOString() },
      ],
    },
  ],
};

const postingHistory: Record<number, CampaignHistoryResponse> = {
  1: {
    summary: {
      postings: 2,
      messages: 5,
      replies: 3,
      mentions: 1,
      dms: 1,
    },
    items: [
      {
        id: 7001,
        status: 'posted',
        scheduledAt: new Date(Date.now() - 90 * 60_000).toISOString(),
        startedAt: new Date(Date.now() - 84 * 60_000).toISOString(),
        finishedAt: new Date(Date.now() - 78 * 60_000).toISOString(),
        lastError: null,
        target: {
          id: 101,
          title: 'Crypto Miners RU',
          normalizedLink: '@cryptominers_ru',
        },
        stats: {
          replyCount: 2,
          mentionCount: 1,
          dmCount: 1,
        },
        postedMessages: [
          {
            id: 1,
            accountId: 11,
            accountName: 'Костя',
            role: 'A',
            text: 'Ребят, кто какой майнинг-пул юзает? Подустал от 2miners — комса кусается.',
            postedAt: new Date(Date.now() - 84 * 60_000).toISOString(),
          },
          {
            id: 2,
            accountId: 12,
            accountName: 'Алекс',
            role: 'B',
            text: 'Я с 44pool второй месяц. Норм заходит.',
            replyToTgId: '1',
            postedAt: new Date(Date.now() - 82 * 60_000).toISOString(),
          },
          {
            id: 3,
            accountId: 11,
            accountName: 'Костя',
            role: 'A',
            text: 'А почему его? Не слышал такого.',
            replyToTgId: '2',
            postedAt: new Date(Date.now() - 80 * 60_000).toISOString(),
          },
        ],
      },
      {
        id: 7002,
        status: 'failed',
        scheduledAt: new Date(Date.now() - 40 * 60_000).toISOString(),
        startedAt: new Date(Date.now() - 34 * 60_000).toISOString(),
        finishedAt: new Date(Date.now() - 29 * 60_000).toISOString(),
        lastError: 'FLOOD_WAIT_180',
        target: {
          id: 102,
          title: 'ASIC Chat',
          normalizedLink: '@asicchat',
        },
        stats: {
          replyCount: 1,
          mentionCount: 0,
          dmCount: 0,
        },
        postedMessages: [
          {
            id: 4,
            accountId: 16,
            accountName: 'Лана',
            role: 'A',
            text: 'Кто-нибудь пробовал 44pool? Думаю свалить с f2pool.',
            postedAt: new Date(Date.now() - 34 * 60_000).toISOString(),
          },
          {
            id: 5,
            accountId: 14,
            accountName: 'Денвер',
            role: 'B',
            text: 'Я там. Выплаты каждый день, минималка низкая.',
            replyToTgId: '4',
            postedAt: new Date(Date.now() - 32 * 60_000).toISOString(),
          },
        ],
      },
    ],
  },
};

export const listInboxThreads = (campaignId: number) => wait(threads[campaignId] ?? []);
export const getCampaignHistory = (campaignId: number) => wait(postingHistory[campaignId] ?? {
  summary: { postings: 0, messages: 0, replies: 0, mentions: 0, dms: 0 },
  items: [],
});
export const listCampaignLogs = (
  campaignId: number,
  scope: 'upcoming' | 'completed',
  type: 'all' | 'join' | 'seed',
): Promise<CampaignLogsResponse> => {
  const campaignTargets = targets[campaignId] ?? [];
  const history = postingHistory[campaignId];

  const joinItems = campaignTargets.map((target, index) => ({
    id: `join-${target.id}`,
    type: 'join' as const,
    status: target.isJoined ? 'joined' : index % 2 === 0 ? 'pending' : 'failed',
    targetId: target.id,
    targetTitle: target.title || target.normalizedLink,
    normalizedLink: target.normalizedLink,
    scheduledAt: new Date(Date.now() - (index + 1) * 3600e3).toISOString(),
    startedAt: null,
    finishedAt: target.isJoined ? new Date(Date.now() - index * 1800e3).toISOString() : null,
    lastError: target.isJoined ? null : index % 2 === 0 ? null : 'Invite expired',
    joinedCount: target.isJoined ? 2 : 0,
    reservedCount: target.isJoined ? 2 : 1,
  }));

  const seedItems = (history?.items ?? []).map((item) => ({
    id: `seed-${item.id}`,
    type: 'seed' as const,
    status: item.status,
    targetId: item.target.id,
    targetTitle: item.target.title || item.target.normalizedLink,
    normalizedLink: item.target.normalizedLink,
    scheduledAt: item.scheduledAt,
    startedAt: item.startedAt,
    finishedAt: item.finishedAt,
    lastError: item.lastError,
    messagesPosted: item.postedMessages.length,
  }));

  const items = [...joinItems, ...seedItems]
    .filter((item) => (type === 'all' ? true : item.type === type))
    .filter((item) => {
      if (scope === 'upcoming') {
        return item.type === 'join'
          ? ['pending', 'requested'].includes(item.status)
          : ['scheduled', 'running'].includes(item.status);
      }
      return item.type === 'join'
        ? !['pending', 'requested'].includes(item.status)
        : ['posted', 'failed', 'cancelled'].includes(item.status);
    })
    .sort((left, right) => {
      const leftTime = new Date(left.finishedAt || left.startedAt || left.scheduledAt).getTime();
      const rightTime = new Date(right.finishedAt || right.startedAt || right.scheduledAt).getTime();
      return rightTime - leftTime;
    });

  return wait({ items });
};
export const getInboxThread = (threadId: string) => {
  const t = Object.values(threads).flat().find((x) => x.id === threadId);
  return wait(t!);
};
export const sendInboxMessage = (threadId: string, text: string) => {
  const t = Object.values(threads).flat().find((x) => x.id === threadId);
  if (!t) return wait({ ok: false } as any);
  const msg: ChatMessage = {
    id: Math.floor(Math.random() * 100000),
    side: 'me',
    text,
    at: new Date().toISOString(),
    status: 'posted',
  };
  t.messages.push(msg);
  t.lastAt = msg.at;
  t.preview = text;
  t.unreadCount = 0;
  return wait(msg, 280);
};


export const deleteCampaign = (id: number) => {
  const idx = campaigns.findIndex((x) => x.id === id);
  if (idx >= 0) campaigns.splice(idx, 1);
  return wait({ ok: true });
};

export const removeTarget = (campaignId: number, targetId: number) => {
  const arr = targets[campaignId] ?? [];
  const idx = arr.findIndex((x) => x.id === targetId);
  if (idx >= 0) arr.splice(idx, 1);
  syncCampaignTargetCount(campaignId);
  return wait({ ok: true });
};

export const detachAccount = (campaignId: number, accountId: number) => {
  const attached = ensureAttachedMap(campaignId);
  attached.delete(accountId);
  syncCampaignAccountCount(campaignId);
  return wait({ ok: true });
};

export const listAttachedAccounts = (campaignId: number) =>
  wait(
    Array.from(ensureAttachedMap(campaignId).entries())
      .map(([accountId, role]) => {
        const account = accounts.find((item) => item.id === accountId);
        return account ? { ...account, role } : null;
      })
      .filter(Boolean) as Account[],
  );

export const listCampaignTeam = (campaignId: number): Promise<CampaignTeamMember[]> => {
  const campaign = campaigns.find((item) => item.id === campaignId);
  const ownerName = campaign?.owner?.firstName || 'Owner';
  return wait([
    {
      userId: campaign?.owner?.userId ?? 1,
      tgId: campaign?.owner?.tgId ?? '1',
      username: campaign?.owner?.username ?? 'owner',
      firstName: ownerName,
      lastName: campaign?.owner?.lastName ?? null,
      role: 'owner',
      invitedBy: null,
      createdAt: campaign?.createdAt ?? new Date().toISOString(),
    },
  ]);
};

export const addCampaignTeamMember = (
  campaignId: number,
  payload: { tgId?: string; username?: string; role?: 'owner' | 'member' },
): Promise<{ ok: boolean; member: CampaignTeamMember }> =>
  wait({
    ok: true,
    member: {
      userId: Date.now(),
      tgId: payload.tgId || '0',
      username: payload.username?.replace(/^@+/, '') ?? null,
      firstName: null,
      lastName: null,
      role: payload.role || 'member',
      invitedBy: null,
      createdAt: new Date().toISOString(),
    },
  });

export const removeCampaignTeamMember = (_campaignId: number, _userId: number) => wait({ ok: true });

export const getTargetDetail = (campaignId: number, targetId: number) => {
  const arr = targets[campaignId] ?? [];
  const t = arr.find((x) => x.id === targetId) ?? arr[0];
  // mock — постингов нет, возвращаем пустой стек
  return wait({ target: t, postings: [] });
};

export const distributeCampaign = (campaignId: number) => {
  const c = campaigns.find((x) => x.id === campaignId);
  if (c) c.status = 'ready';
  return wait({
    ok: true,
    status: 'ready' as const,
    accounts: accounts.filter((a) => a.status === 'active').length,
    targets: (targets[campaignId] ?? []).length,
    postingsCreated: 0,
    joins: { scheduled: 0, targets: 0, accounts: 0 },
  });
};

export const createCampaign = (data: Partial<Campaign>): Promise<Campaign> => {
  const c: Campaign = {
    id: Math.max(0, ...campaigns.map((x) => x.id)) + 1,
    name: data.name || 'Без названия',
    status: 'draft',
    dialogPrompt: data.dialogPrompt,
    dmReplyPrompt: data.dmReplyPrompt,
    scheduleStartHour: data.scheduleStartHour ?? 10,
    scheduleEndHour: data.scheduleEndHour ?? 22,
    intervalMinutes: data.intervalMinutes ?? 60,
    maxChatsPerAccountPerDay: data.maxChatsPerAccountPerDay ?? 8,
    minDelayBetweenChatsSec: data.minDelayBetweenChatsSec ?? 600,
    joinDelayMinSec: data.joinDelayMinSec ?? 60,
    joinDelayMaxSec: data.joinDelayMaxSec ?? 240,
    typingMinMs: data.typingMinMs ?? 2500,
    typingMaxMs: data.typingMaxMs ?? 9000,
    seedStepDelayMinSec: data.seedStepDelayMinSec ?? 20,
    seedStepDelayMaxSec: data.seedStepDelayMaxSec ?? 180,
    chatReplyDelayMinSec: data.chatReplyDelayMinSec ?? 20,
    chatReplyDelayMaxSec: data.chatReplyDelayMaxSec ?? 180,
    dmReplyDelayMinSec: data.dmReplyDelayMinSec ?? 60,
    dmReplyDelayMaxSec: data.dmReplyDelayMaxSec ?? 300,
    maxChatRepliesPerUser: data.maxChatRepliesPerUser ?? 1,
    maxDmRepliesPerDialog: data.maxDmRepliesPerDialog ?? 1,
    seedMessagesMin: data.seedMessagesMin ?? 2,
    seedMessagesMax: data.seedMessagesMax ?? 5,
    uniqueDialogPerChat: data.uniqueDialogPerChat ?? true,
    createdAt: new Date().toISOString(),
    counts: { targets: 0, accounts: 0, dialogs: 0, posts: 0, pings: 0, dms: 0 },
    seeding: {
      readyTargets: 0,
      trackedTargets: 0,
      completedTargets: 0,
      postedTargets: 0,
      activeTargets: 0,
      failedTargets: 0,
      tasksScheduled: 0,
      tasksRunning: 0,
      tasksPosted: 0,
      tasksFailed: 0,
      targetStatuses: [],
    },
  };
  campaigns.unshift(c);
  attachedAccountsByCampaign.set(c.id, new Map());
  return wait(c, 320);
};

function ensureAttachedMap(campaignId: number): Map<number, CampaignAccountRole> {
  let attached = attachedAccountsByCampaign.get(campaignId);
  if (!attached) {
    attached = new Map();
    attachedAccountsByCampaign.set(campaignId, attached);
  }
  return attached;
}

function syncCampaignAccountCount(campaignId: number) {
  const campaign = campaigns.find((item) => item.id === campaignId);
  if (!campaign) return;
  campaign.counts.accounts = ensureAttachedMap(campaignId).size;
}

function syncCampaignTargetCount(campaignId: number) {
  const campaign = campaigns.find((item) => item.id === campaignId);
  if (!campaign) return;
  campaign.counts.targets = (targets[campaignId] ?? []).length;
}

const accountDialogs: Record<number, AccountDialog[]> = {
  11: [
    {
      id: '301',
      campaignId: 1,
      campaignName: 'Mining pool · 44pool RU',
      accountUsername: '@kostya_88',
      fromName: 'Виктор',
      fromUsername: '@vk_sk',
      kind: 'mention',
      unreadCount: 1,
      lastAt: new Date(Date.now() - 16 * 60_000).toISOString(),
      preview: '@kostya_88 а ты сам реально на 44pool сидишь?',
    },
  ],
  12: [
    {
      id: '302',
      campaignId: 1,
      campaignName: 'Mining pool · 44pool RU',
      accountUsername: '@alex.miner',
      fromName: 'Игорь Сипкин',
      fromUsername: '@isipkin',
      kind: 'dm',
      unreadCount: 0,
      lastAt: new Date(Date.now() - 8 * 60_000).toISOString(),
      preview: 'Привет! Можешь рефку на 44pool кинуть?',
    },
  ],
};

const accountLogItems: Record<number, AccountLogEntry[]> = {
  11: [
    {
      id: 'join-9001',
      type: 'join',
      status: 'pending',
      campaignId: 1,
      campaignName: 'Mining pool · 44pool RU',
      targetId: 101,
      targetTitle: 'Crypto Miners RU',
      scheduledAt: new Date(Date.now() + 22 * 60_000).toISOString(),
      startedAt: null,
      finishedAt: null,
      lastError: null,
    },
    {
      id: 'seed-9002',
      type: 'seed',
      status: 'posted',
      campaignId: 1,
      campaignName: 'Mining pool · 44pool RU',
      targetId: 102,
      targetTitle: 'ASIC Chat',
      scheduledAt: new Date(Date.now() - 80 * 60_000).toISOString(),
      startedAt: new Date(Date.now() - 72 * 60_000).toISOString(),
      finishedAt: new Date(Date.now() - 66 * 60_000).toISOString(),
      lastError: null,
    },
  ],
  12: [
    {
      id: 'seed-9101',
      type: 'seed',
      status: 'running',
      campaignId: 1,
      campaignName: 'Mining pool · 44pool RU',
      targetId: 103,
      targetTitle: 'Farm Talk',
      scheduledAt: new Date(Date.now() + 10 * 60_000).toISOString(),
      startedAt: null,
      finishedAt: null,
      lastError: null,
    },
  ],
};

export const checkAccount = (accountId: number): Promise<AccountCheck> => {
  const account = accounts.find((item) => item.id === accountId) ?? accounts[0];
  const dialogs = accountDialogs[accountId] ?? [];
  return wait({
    account,
    proxy: {
      configured: Boolean(account.proxy),
      connection: { ok: true },
      externalIp: account.proxy?.ip,
    },
    telegram: {
      ok: true,
      me: {
        telegramId: String(account.id + 100000),
        firstName: account.name?.split(' ')[0] || account.username,
        lastName: account.name?.split(' ').slice(1).join(' ') || undefined,
        username: account.username,
        phone: account.phone,
        avatarDataUrl: null,
      },
    },
    runtime: {
      status: account.status,
      floodWaitSeconds: account.cooldownUntil
        ? Math.max(0, Math.round((new Date(account.cooldownUntil).getTime() - Date.now()) / 1000))
        : 0,
      cooldownUntil: account.cooldownUntil ?? null,
      proxyErrorCount: 0,
    },
    dialogs: {
      total: dialogs.length,
      unread: dialogs.filter((dialog) => dialog.unreadCount > 0).length,
    },
  });
};

export const listAccountDialogs = (accountId: number) =>
  wait(accountDialogs[accountId] ?? []);

export const listAccountLogs = (
  accountId: number,
  scope: 'upcoming' | 'completed',
  page = 1,
  pageSize = 20,
): Promise<PaginatedAccountLogs> => {
  const all = (accountLogItems[accountId] ?? []).filter((item) =>
    scope === 'upcoming'
      ? item.status === 'pending' || item.status === 'scheduled' || item.status === 'running'
      : !['pending', 'scheduled', 'running'].includes(item.status),
  );
  const start = (page - 1) * pageSize;
  const items = all.slice(start, start + pageSize);
  return wait({
    items,
    page,
    pageSize,
    total: all.length,
    totalPages: Math.max(1, Math.ceil(all.length / pageSize)),
  });
};
